import React, { useMemo } from 'react'
import { Container, Navbar } from 'react-bootstrap'
import { TfiAngleLeft } from 'react-icons/tfi'
import { Link, useLocation } from 'react-router-dom'
import { useAppVersion } from '../../../../hooks/useAppVersion'

const RE_PATH = /^\/(?:app\/([^/]+)(?:\/([^/]+)(?:\/([^/]+))?)?)?$/i

function getBackPathname(pathname: string): string | undefined {
    const result = pathname.match(RE_PATH)

    if (result) {
        const [, projectId, workflowName, installationId] = result

        if (projectId) {
            if (workflowName) {
                if (workflowName === 'workflows') {
                    return '/'
                } else {
                    if (installationId) {
                        return `/app/${projectId}/${workflowName}`
                    } else {
                        return `/app/${projectId}/workflows`
                    }
                }
            } else {
                return undefined
            }
        } else {
            return undefined
        }
    } else {
        return undefined
    }
}

interface LayoutProps {
    children: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
    const location = useLocation()
    const { lastUpdated } = useAppVersion()

    const backPathname = useMemo<string | undefined>(() => {
        return getBackPathname(location.pathname)
    }, [location.pathname])

    return (
        <div id="root-background">
            <Navbar id="root-banner">
                <div id="navbar-top-row">
                    <div id="pnnl-logo-container">
                        <img
                            src={`${process.env.PUBLIC_URL}/PNNL_Logo.jpg`}
                            alt="PNNL"
                            id="pnnl-logo"
                        />
                    </div>
                    <Container id="root-flex-layout">
                        <Navbar.Brand>
                            <div id="root-title-container">
                                <span id="root-title">
                                    {process.env.REACT_APP_NAME}
                                    {process.env.REACT_APP_TEST_SITE
                                        ? ' (Test Site)'
                                        : ''}
                                </span>
                                <span id="root-subtitle">
                                    {process.env.REACT_APP_SUBTITLE}
                                </span>
                            </div>
                        </Navbar.Brand>
                    </Container>
                </div>
                {backPathname && (
                    <div id="navbar-second-row">
                        <Link to={backPathname} id="back-text-link">
                            <TfiAngleLeft id="back-icon" />
                            Back
                        </Link>
                        <Link to="/" id="home-text-link">
                            Home
                        </Link>
                    </div>
                )}
            </Navbar>
            <div id="root-body">{children}</div>
            <footer id="app-footer">
                <Container>
                    <div id="footer-content">
                        <div id="footer-links">
                            <a
                                href={process.env.REACT_APP_HOMEPAGE}
                                id="about-link"
                                target="_blank"
                                rel="noreferrer"
                            >
                                About
                            </a>
                            <span id="footer-separator">|</span>
                            <a
                                href={
                                    process.env
                                        .REACT_APP_SECURITY_AND_PRIVACY_STATEMENT
                                }
                                id="security-link"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Security & Privacy Statement
                            </a>
                            <span id="footer-separator">|</span>
                            <a
                                href={process.env.REACT_APP_ACCESSIBILITY}
                                id="accessibility-link"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Accessibility Statement
                            </a>
                            <span id="footer-separator">|</span>
                            <a
                                href={process.env.REACT_APP_CONTACT_US}
                                id="contact-link"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Contact Us
                            </a>
                        </div>
                        <div id="footer-date">
                            Last Updated:{' '}
                            {lastUpdated || new Date().toLocaleDateString()}
                        </div>
                    </div>
                </Container>
            </footer>
        </div>
    )
}

export default Layout
